import { useEffect, useMemo, useCallback } from "react";
import { get, startsWith, throttle } from "lodash";
import { NotificationManager } from "react-notifications";
import { useTranslation } from "react-i18next";
import { useGetLatest } from "../hooks";

export const useApiErrorHandler = ({ wait = 2000 } = {}) => {
  const { t } = useTranslation();

  const notifications = useMemo(() => {
    const serverErrorNotification = throttle(
      () => NotificationManager.error(t("An unexpected error occurred")),
      wait
    );
    const clientErrorNotification = throttle(
      () =>
        NotificationManager.error(
          t("An error occurred, please check the provided input and try again")
        ),
      wait
    );
    const clientErrorBadRequestNotification = throttle(
      (data) =>{
        const errorMessage = Object.entries(data).map( ([key, value]) => {
          if(key !=='code') return Array.isArray(value) ? value[0] : value;
        })[0];
        NotificationManager.error(
          t(`${errorMessage}`)
        )},
      wait
    );
    const connectionNotification = throttle(
      () =>
        NotificationManager.error(
          t(
            "You may be offline, please check your internet connection and try again"
          )
        ),
      wait
    );

    const notFoundNotification = throttle(
      () =>
        NotificationManager.warning(
          t("The requested data could not be found for this organisation")
        ),
      wait
    );

    return {
      serverErrorNotification,
      clientErrorNotification,
      connectionNotification,
      notFoundNotification,
      clientErrorBadRequestNotification,
    };
  }, [t, wait]);

  const getNotifications = useGetLatest(notifications);

  const handle = useCallback(
    (ex, { rethrow = true } = {}) => {
      const {
        serverErrorNotification,
        clientErrorNotification,
        connectionNotification,
        notFoundNotification,
        clientErrorBadRequestNotification,
      } = getNotifications();

      const status = get(ex, "response.status", null);
      const data = get(ex, "response.data", null);

      if (status >= 500 && status < 600) {
        serverErrorNotification();
      } else if (status === 404) {
        notFoundNotification();
      } else if (status === 400) {
        clientErrorBadRequestNotification(data);
      } else if (status > 400 && status < 500) {
        clientErrorNotification();
      } else if (status === null) {
        connectionNotification();
      } else {
        if (rethrow) {
          throw ex;
        }
        return false;
      }

      return true;
    },
    [getNotifications]
  );

  return { handle };
};

export const ApiUnhandledListener = (props) => {
  const { handle } = useApiErrorHandler(props);

  const handler = useCallback(
    (e) => {
      const reason = e?.reason ?? e.detail?.reason ?? null;
      if (!reason || !startsWith(reason.config?.baseURL, "/api")) return;

      if (reason.handled) {
        e.preventDefault();
        return;
      }

      if (handle(reason, { rethrow: false })) {
        e.preventDefault();
      }
    },
    [handle]
  );

  useEffect(() => {
    window.addEventListener("unhandledrejection", handler);
    return () => window.removeEventListener("unhandledrejection", handler);
  }, [handler]);

  return null;
};
